import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, Link as LinkIcon, Camera } from 'lucide-react';
import api, { absoluteAsset } from '../services/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const ImageUpload = ({ value, onChange, label = 'Imagen', aspect = 'square' }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const { toast } = useToast();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast({ title: 'Archivo no válido', description: 'Solo se permiten imágenes' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast({ title: 'Imagen subida correctamente' });
    } catch (e) {
      toast({ title: 'Error al subir', description: e?.response?.data?.detail || 'Intenta de nuevo' });
    } finally {
      setUploading(false);
    }
  };

  // Drag & drop handlers
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  // Paste from clipboard
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) {
          handleFile(f);
          e.preventDefault();
          break;
        }
      }
    }
  };

  const preview = value ? absoluteAsset(value) : '';
  const aspectClass = aspect === 'wide' ? 'h-32' : 'h-40';

  return (
    <div className="space-y-2" onPaste={onPaste}>
      <label className="text-sm font-medium text-slate-700">{label}</label>

      <div
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !preview && !uploading && fileRef.current?.click()}
        className={`relative flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed bg-slate-50 transition-colors ${aspectClass} ${
          dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'
        }`}
      >
        {preview ? (
          <div className="relative h-full w-full">
            <img src={preview} alt="preview" className="h-full w-full object-contain p-2" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="absolute right-2 top-2 rounded-full bg-white/90 p-1 text-slate-700 shadow-sm hover:bg-white"
              aria-label="Quitar imagen"
              data-testid="image-remove-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 px-4 text-center text-slate-400">
            {uploading ? (
              <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
            ) : (
              <Upload className={`h-7 w-7 ${dragOver ? 'text-emerald-600' : ''}`} />
            )}
            <span className="text-xs">
              {uploading
                ? 'Subiendo...'
                : dragOver
                ? 'Suelta la imagen aquí'
                : 'Arrastra una imagen o haz clic'}
            </span>
            <span className="text-[10px] text-slate-400">También puedes pegar (Ctrl+V)</span>
          </div>
        )}
      </div>

      {!urlMode ? (
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              handleFile(f);
              e.target.value = '';
            }}
            data-testid="image-file-input"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              handleFile(f);
              e.target.value = '';
            }}
            data-testid="image-camera-input"
          />
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="h-9 gap-2 border-slate-300 bg-white"
            data-testid="image-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Subiendo...' : 'Archivo'}
          </Button>
          <Button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="h-9 gap-2 border-slate-300 bg-white md:hidden"
            data-testid="image-camera-btn"
          >
            <Camera className="h-4 w-4" />
            Cámara
          </Button>
          <Button
            type="button"
            onClick={() => setUrlMode(true)}
            variant="outline"
            className="h-9 gap-2 border-slate-300 bg-white"
            data-testid="image-url-btn"
          >
            <LinkIcon className="h-4 w-4" />
            URL
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            placeholder="https://..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="h-9"
          />
          <Button
            type="button"
            onClick={() => { if (urlInput.trim()) { onChange(urlInput.trim()); setUrlMode(false); setUrlInput(''); } }}
            className="h-9 bg-emerald-700 text-white hover:bg-emerald-800"
          >
            Usar
          </Button>
          <Button type="button" onClick={() => setUrlMode(false)} variant="outline" className="h-9">
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
